import { useMemo } from 'react';

import { Doughnut } from 'react-chartjs-2';
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';

import { countries } from 'country-flags-svg';

import { Flag } from 'iconsax-react';

import classes from './ReviewsCountries.module.scss';

ChartJS.register(ArcElement, Tooltip, Legend);

// Teal palette for countries, orange reserved for the "others" bucket.
const COUNTRY_COLORS = ['#18BBA8', '#4ECDC4', '#7FDCD3', '#A8E7E1', '#CFF0EC'];
const OTHERS_COLOR = '#FAA61A';

const buildRows = (countriesMap = {}) => {
	const entries = Object.entries(countriesMap);
	let colorIndex = 0;

	return entries.map(([code, percentage]) => {
		const isOthers = code.toLowerCase() === 'others';
		const country = !isOthers
			? countries.find((item) => item.iso2 === code.toUpperCase())
			: null;

		return {
			code,
			percentage,
			isOthers,
			name: isOthers ? 'Others' : country?.name || code,
			iso2: country?.iso2?.toLowerCase() ?? null,
			color: isOthers ? OTHERS_COLOR : COUNTRY_COLORS[colorIndex++ % COUNTRY_COLORS.length]
		};
	});
};

const ReviewsCountries = ({ summary }) => {
	const rows = useMemo(() => buildRows(summary.countries), [summary.countries]);

	const countriesCount = summary.countries_with_reviews;

	const chartData = {
		labels: rows.map((row) => row.name),
		datasets: [
			{
				data: rows.map((row) => row.percentage),
				backgroundColor: rows.map((row) => row.color),
				borderWidth: 0,
				hoverOffset: 4
			}
		]
	};

	const chartOptions = {
		cutout: '72%',
		responsive: true,
		maintainAspectRatio: false,
		plugins: {
			legend: { display: false },
			tooltip: {
				callbacks: {
					label: (context) => `${context.label}: ${context.parsed}%`
				}
			}
		}
	};

	return (
		<div className={classes['card']}>
			<div className={classes['card-header']}>
				<h3 className={classes['card-title']}>Top Countries by Reviews</h3>
				<span className={classes['countries-count']}>
					<Flag size={18} color="#faa61a" variant="Bold" />
					<strong>{countriesCount}</strong> Countries in Reviews
				</span>
			</div>

			<div className={classes['card-body']}>
				<div className={classes['chart-wrapper']}>
					<Doughnut data={chartData} options={chartOptions} />
					<div className={classes['chart-center']}>
						<span className={classes['chart-center-value']}>
							{(summary.total_lecture_reviews ?? 0).toLocaleString('en-US')}
						</span>
						<span className={classes['chart-center-label']}>Total Reviews</span>
					</div>
				</div>

				<ul className={classes['country-list']}>
					{rows.map((row) => (
						<li key={row.code} className={classes['country-row']}>
							<span className={classes['country-name']}>
								{row.iso2 ? (
									<span className={`fi fi-${row.iso2} ${classes['country-flag']}`} />
								) : (
									<span
										className={classes['country-dot']}
										style={{ backgroundColor: row.color }}
									/>
								)}
								{row.name}
							</span>
							<span className={classes['country-percentage']}>{row.percentage}%</span>
						</li>
					))}
				</ul>
			</div>
		</div>
	);
};

export default ReviewsCountries;
